import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { EditableLeafletMap, EditableLeafletMapRef } from '../../components/map';
import { usePermission } from '../../components/PermissionGuard';

interface ModificationItem {
  id: number;
  targetFile: string;
  targetFeatureId: string;
  fieldPath: string;
  oldValue: any;
  newValue: any;
  operationType: 'create' | 'update' | 'delete';
  geometry?: any;
}

interface ConflictItem {
  itemId: number;
  fieldPath: string;
  expectedValue: any;
  currentLiveValue: any;
  proposedValue: any;
}

interface ProposalDetails {
  id: number;
  proposalTitle?: string;
  proposalDescription?: string;
  status: string;
  proposer: {
    id: string;
    name: string;
    email: string;
  };
  imdfFile: {
    id: string;
    originalName: string;
  };
  submittedAt: string;
  modificationItems: ModificationItem[];
}

const ReviewDetail: React.FC = () => {
  const { proposalId } = useParams<{ proposalId: string }>();
  const navigate = useNavigate();
  const { isAdmin } = usePermission();
  const mapRef = useRef<EditableLeafletMapRef>(null);

  const [proposal, setProposal] = useState<ProposalDetails | null>(null);
  const [geoJsonData, setGeoJsonData] = useState<GeoJSON.FeatureCollection | null>(null);
  const [originalGeoJsonData, setOriginalGeoJsonData] = useState<GeoJSON.FeatureCollection | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<ModificationItem | null>(null);
  
  // 審核操作狀態
  const [isProcessing, setIsProcessing] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  
  // 衝突處理狀態
  const [conflicts, setConflicts] = useState<ConflictItem[]>([]);
  const [showConflictResolution, setShowConflictResolution] = useState(false);
  const [conflictResolutions, setConflictResolutions] = useState<Record<number, any>>({});

  useEffect(() => {
    const hasAdminPermission = isAdmin();
    
    if (!hasAdminPermission) {
      setError('需要 Admin 權限才能審核提案');
      setIsLoading(false);
      return;
    }
    
    loadProposalDetails();
  }, [proposalId]); // 只依賴 proposalId

  const loadProposalDetails = async () => {
    setIsLoading(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://imdf.sto-tek.com';
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/proposals/${proposalId}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (response.ok) {
        const result = await response.json();
        setProposal(result.data);

        // 載入原始 IMDF 檔案的 GeoJSON 資料
        await loadOriginalIMDFData(result.data.imdfFile.id, result.data.modificationItems || []);
      } else {
        setError('載入提案詳情失敗');
      }
    } catch (err) {
      setError('載入提案詳情失敗');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadOriginalIMDFData = async (imdfFileId: string, modificationItems: ModificationItem[] = []) => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://imdf.sto-tek.com';
      const token = localStorage.getItem('token');

      // 使用現有的下載端點獲取 ZIP 檔案
      const response = await fetch(`${API_URL}/imdf/files/${imdfFileId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (response.ok) {
        const blob = await response.blob();

        // 使用 JSZip 解壓縮檔案（需要先引入 JSZip）
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        const zipFile = await zip.loadAsync(blob);

        const allFeatures: GeoJSON.Feature[] = [];

        // 處理 ZIP 檔案中的每個 GeoJSON 檔案
        for (const fileName in zipFile.files) {
          const file = zipFile.files[fileName];
          if (file.dir) continue;

          const fileExtension = fileName.split('.').pop()?.toLowerCase();
          if (fileExtension === 'geojson') {
            try {
              const content = await file.async('text');
              const geoJson = JSON.parse(content);

              if (geoJson.features) {
                // 為每個特徵添加來源檔案資訊
                const fileType = fileName.split('/').pop()?.replace('.geojson', '') || 'unknown';

                geoJson.features.forEach((feature: GeoJSON.Feature) => {
                  feature.properties = {
                    ...feature.properties,
                    _sourceFile: fileName,
                    _file_type: fileType
                  };
                  allFeatures.push(feature);
                });
              }
            } catch (parseError) {
              console.warn(`Failed to parse ${fileName}:`, parseError);
            }
          }
        }

        const geoJsonData: GeoJSON.FeatureCollection = {
          type: 'FeatureCollection',
          features: allFeatures
        };

        setOriginalGeoJsonData(geoJsonData);

        // 構建包含原始特徵和修改標示的地圖資料
        buildMapDisplayData(geoJsonData, modificationItems);
      } else {
        console.warn('無法載入原始 IMDF 資料');
      }
    } catch (err) {
      console.warn('載入原始 IMDF 資料失敗:', err);
    }
  };

  const buildMapDisplayData = (originalData: GeoJSON.FeatureCollection, modificationItems: ModificationItem[]) => {
    const modifiedFeatureIds = new Set(modificationItems.map(item => item.targetFeatureId));
    const modificationMap = new Map<string, ModificationItem[]>();

    // 建立修改項目的映射
    modificationItems.forEach(item => {
      const existing = modificationMap.get(item.targetFeatureId) || [];
      existing.push(item);
      modificationMap.set(item.targetFeatureId, existing);
    });

    const mapFeatures: GeoJSON.Feature[] = [];
    const relevantUnitIds = new Set<string>();

    // 1. 找出所有相關的 amenity/occupant 和它們關聯的 unit IDs
    modificationItems.forEach(item => {
      const targetFileType = item.targetFile.replace('.geojson', '').toLowerCase();

      if (targetFileType === 'amenity' || targetFileType === 'occupant') {
        // 找到該特徵的完整資料
        const sourceFeature = originalData.features.find(
          f => (f.id || f.properties?.id) === item.targetFeatureId &&
               (f.properties?._file_type === targetFileType || f.properties?._sourceFile?.includes(targetFileType))
        );

        if (sourceFeature) {
          if (targetFileType === 'amenity') {
            // amenity 通過 unit_ids 關聯
            const unitIds = sourceFeature.properties?.unit_ids;
            if (Array.isArray(unitIds)) {
              unitIds.forEach(unitId => relevantUnitIds.add(String(unitId)));
            }
          } else if (targetFileType === 'occupant') {
            // occupant 通過 anchor_id 關聯到 anchor，但我們也想顯示相關的 unit
            // 可以通過 anchor 的位置找到對應的 unit（這部分邏輯可以根據需要調整）
          }
        }
      }
    });

    // 2. 選擇要顯示的特徵
    originalData.features.forEach(feature => {
      if (!feature.geometry) return;

      const featureId = String(feature.id || feature.properties?.id);
      const fileType = feature.properties?._file_type;
      const isModified = modifiedFeatureIds.has(featureId);

      // 決定是否顯示這個特徵
      let shouldShow = false;

      if (fileType === 'venue' || fileType === 'level') {
        // 總是顯示 venue 和 level（提供空間上下文）
        shouldShow = true;
      } else if (fileType === 'amenity' || fileType === 'occupant') {
        // 只顯示被修改的 amenity/occupant
        shouldShow = isModified;
      } else if (fileType === 'unit') {
        // 只顯示與被修改的 amenity 相關的 unit
        shouldShow = relevantUnitIds.has(featureId);
      } else if (fileType === 'anchor') {
        // 如果有被修改的 occupant，顯示相關的 anchor
        const hasModifiedOccupant = modificationItems.some(item =>
          item.targetFile.includes('occupant') &&
          originalData.features.some(f =>
            f.properties?.anchor_id === featureId &&
            (f.id || f.properties?.id) === item.targetFeatureId
          )
        );
        shouldShow = hasModifiedOccupant;
      }
      // 其他類型的特徵（opening, fixture 等）預設不顯示，除非被修改
      else {
        shouldShow = isModified;
      }

      if (shouldShow) {
        mapFeatures.push({
          ...feature,
          properties: {
            ...feature.properties,
            _is_modified: isModified,
            _modification_items: isModified ? modificationMap.get(featureId) : undefined
          }
        });
      }
    });

    // 3. 添加有幾何資料的修改項目（作為覆蓋層顯示）
    modificationItems.forEach(item => {
      if (item.geometry) {
        mapFeatures.push({
          type: 'Feature',
          properties: {
            id: item.targetFeatureId,
            _file_type: item.targetFile.replace('.geojson', ''),
            _modification_item_id: item.id,
            _operation_type: item.operationType,
            _is_modification_overlay: true
          },
          geometry: item.geometry
        });
      }
    });

    setGeoJsonData({
      type: 'FeatureCollection',
      features: mapFeatures
    });
  };

  const handleItemClick = (item: ModificationItem) => {
    setSelectedItem(item);

    // 在地圖上聚焦到該特徵
    if (mapRef.current) {
      const feature = findFeatureForPositioning(item);
      if (feature) {
        mapRef.current.focusOnFeature(feature);
      }
    }
  };

  // 找到用於定位的特徵（包括通過 unit_ids 關聯的特徵）
  const findFeatureForPositioning = (item: ModificationItem): GeoJSON.Feature | null => {
    // 1. 優先使用修改項目自己的幾何資料
    if (item.geometry) {
      return {
        type: 'Feature',
        properties: { id: item.targetFeatureId },
        geometry: item.geometry
      };
    }

    // 2. 如果沒有幾何資料，從原始 IMDF 資料中查找
    if (!originalGeoJsonData) {
      console.warn('原始 IMDF 資料尚未載入');
      return null;
    }

    // 2a. 先直接找該特徵
    const directFeature = originalGeoJsonData.features.find(
      f => (f.id || f.properties?.id) === item.targetFeatureId
    );

    if (directFeature && directFeature.geometry) {
      return directFeature;
    }

    // 2b. 如果是 amenity 或 occupant，通過關聯的 unit 或 anchor 來定位
    const targetFileType = item.targetFile.replace('.geojson', '').toLowerCase();

    if (targetFileType === 'amenity' || targetFileType === 'occupant') {
      // 找到該 amenity/occupant 特徵的完整資料
      const sourceFeature = originalGeoJsonData.features.find(
        f => (f.id || f.properties?.id) === item.targetFeatureId &&
             (f.properties?._file_type === targetFileType || f.properties?._sourceFile?.includes(targetFileType))
      );

      if (sourceFeature) {
        if (targetFileType === 'amenity') {
          // amenity 通過 unit_ids 關聯
          const unitIds = sourceFeature.properties?.unit_ids;
          if (Array.isArray(unitIds) && unitIds.length > 0) {
            // 找到第一個有幾何資料的關聯 unit
            for (const unitId of unitIds) {
              const unitFeature = originalGeoJsonData.features.find(
                f => (f.id || f.properties?.id) === unitId &&
                     (f.properties?._file_type === 'unit' || f.properties?._sourceFile?.includes('unit'))
              );
              if (unitFeature && unitFeature.geometry) {
                return unitFeature;
              }
            }
          }
        } else if (targetFileType === 'occupant') {
          // occupant 通過 anchor_id 關聯
          const anchorId = sourceFeature.properties?.anchor_id;
          if (anchorId) {
            const anchorFeature = originalGeoJsonData.features.find(
              f => (f.id || f.properties?.id) === anchorId &&
                   (f.properties?._file_type === 'anchor' || f.properties?._sourceFile?.includes('anchor'))
            );
            if (anchorFeature && anchorFeature.geometry) {
              return anchorFeature;
            }
          }
        }
      }
    }

    console.warn(`無法找到修改項目的定位資料: ${item.targetFeatureId}`);
    return null;
  };

  const handleApprove = async () => {
    if (!proposal) return;
    
    setIsProcessing(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://imdf.sto-tek.com';
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/proposals/${proposal.id}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reviewNotes })
      });

      if (response.status === 409) {
        // 有衝突
        const result = await response.json();
        setConflicts(result.data);
        setShowConflictResolution(true);
      } else if (response.ok) {
        // 成功批准
        alert('提案已成功批准並套用變更');
        navigate('/proposals/review');
      } else {
        setError('批准提案失敗');
      }
    } catch (err) {
      setError('批准提案失敗');
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!proposal || !reviewNotes.trim()) {
      setError('拒絕提案時必須提供審核意見');
      return;
    }
    
    setIsProcessing(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://imdf.sto-tek.com';
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/proposals/${proposal.id}/reject`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reviewNotes })
      });

      if (response.ok) {
        alert('提案已拒絕');
        navigate('/proposals/review');
      } else {
        setError('拒絕提案失敗');
      }
    } catch (err) {
      setError('拒絕提案失敗');
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResolveConflicts = async () => {
    if (!proposal) return;
    
    const resolutions = conflicts.map(conflict => ({
      itemId: conflict.itemId,
      resolvedValue: conflictResolutions[conflict.itemId] ?? conflict.proposedValue
    }));

    setIsProcessing(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://imdf.sto-tek.com';
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/proposals/${proposal.id}/resolve-conflicts`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          resolutions,
          reviewNotes 
        })
      });

      if (response.ok) {
        alert('衝突已解決，提案已批准');
        navigate('/proposals/review');
      } else {
        setError('解決衝突失敗');
      }
    } catch (err) {
      setError('解決衝突失敗');
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">null</span>;
    }
    
    if (typeof value === 'object') {
      return (
        <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-20">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }
    
    return <span className="break-words">{String(value)}</span>;
  };

  const getFieldDisplayName = (fieldPath: string) => {
    const parts = fieldPath.split('.');
    return parts.slice(1).join('.'); // 移除 'properties' 前綴
  };

  const getOperationColor = (operation: string) => {
    switch (operation) {
      case 'create': return 'bg-green-100 text-green-800';
      case 'update': return 'bg-blue-100 text-blue-800';
      case 'delete': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getOperationText = (operation: string) => {
    switch (operation) {
      case 'create': return '新增';
      case 'update': return '修改';
      case 'delete': return '刪除';
      default: return '未知';
    }
  };

  if (!isAdmin()) {
    return (
      <div className="p-8 text-center">
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
          需要 Admin 權限才能審核提案
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">載入提案詳情中...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
          {error}
        </div>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="p-8 text-center text-gray-500">
        找不到提案資料
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* 標題列 */}
      <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              審核提案: {proposal.proposalTitle || `提案 #${proposal.id}`}
            </h1>
            <div className="text-sm text-gray-600 mt-1">
              <span>提案人: {proposal.proposer.name}</span>
              <span className="mx-2">•</span>
              <span>IMDF 檔案: {proposal.imdfFile.originalName}</span>
              <span className="mx-2">•</span>
              <span>{proposal.modificationItems.length} 項修改</span>
            </div>
          </div>
          <button
            onClick={() => navigate('/proposals/review')}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
          >
            返回列表
          </button>
        </div>
        
        {proposal.proposalDescription && (
          <div className="mt-3 p-3 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-700">{proposal.proposalDescription}</p>
          </div>
        )}
      </div>

      {/* 主要內容區域 - 左右分欄 */}
      <div className="flex-1 flex">
        {/* 左側 - 地圖 */}
        <div className="flex-1 relative">
          {geoJsonData && (
            <EditableLeafletMap
              ref={mapRef}
              geoJsonData={geoJsonData}
              height="100%"
              className="w-full h-full"
              mode="view"
              enableClustering={false}
              fitBounds={true}
            />
          )}
          
          {!geoJsonData && (
            <div className="flex items-center justify-center h-full bg-gray-100">
              <p className="text-gray-500">無地圖資料可顯示</p>
            </div>
          )}
        </div>

        {/* 右側 - 修改項目列表和審核控制 */}
        <div className="w-96 bg-white shadow-sm border-l flex flex-col">
          {/* 修改項目列表 */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 border-b">
              <h2 className="font-semibold text-gray-900">修改項目列表</h2>
              <p className="text-sm text-gray-600">點擊項目可在地圖上定位（包括通過關聯位置自動定位）</p>
            </div>

            <div className="p-4 space-y-4">
              {proposal.modificationItems.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                    selectedItem?.id === item.id 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {/* 操作類型和檔案 */}
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getOperationColor(item.operationType)}`}>
                      {getOperationText(item.operationType)}
                    </span>
                    <span className="text-xs text-gray-500">{item.targetFile}</span>
                  </div>

                  {/* 特徵和欄位 */}
                  <div className="text-sm space-y-1 mb-3">
                    <div>
                      <span className="text-gray-500">特徵:</span>
                      <span className="ml-1 font-mono text-xs">{item.targetFeatureId}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">欄位:</span>
                      <span className="ml-1 font-medium">{getFieldDisplayName(item.fieldPath)}</span>
                    </div>
                  </div>

                  {/* 並排對比 */}
                  <div className="space-y-2">
                    {item.operationType === 'update' && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-xs text-gray-500 block">原始值</span>
                          <div className="mt-1 p-2 bg-gray-50 rounded text-xs">
                            {formatValue(item.oldValue)}
                          </div>
                        </div>
                        <div>
                          <span className="text-xs text-blue-600 block">提案值</span>
                          <div className="mt-1 p-2 bg-blue-50 rounded text-xs">
                            {formatValue(item.newValue)}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {item.operationType === 'create' && (
                      <div>
                        <span className="text-xs text-green-600 block">新增值</span>
                        <div className="mt-1 p-2 bg-green-50 rounded text-xs">
                          {formatValue(item.newValue)}
                        </div>
                      </div>
                    )}
                    
                    {item.operationType === 'delete' && (
                      <div>
                        <span className="text-xs text-red-600 block">刪除值</span>
                        <div className="mt-1 p-2 bg-red-50 rounded text-xs">
                          {formatValue(item.oldValue)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 審核控制面板 */}
          <div className="border-t bg-gray-50 p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                審核意見
              </label>
              <textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="請輸入審核意見..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
              />
            </div>

            {!showConflictResolution && (
              <div className="flex gap-2">
                <button
                  onClick={handleApprove}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {isProcessing ? '處理中...' : '批准'}
                </button>
                <button
                  onClick={handleReject}
                  disabled={isProcessing || !reviewNotes.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  拒絕
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 衝突解決模態視窗 */}
      {showConflictResolution && conflicts.length > 0 && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium text-gray-900">解決資料衝突</h3>
              <p className="text-sm text-gray-600 mt-1">
                檢測到 {conflicts.length} 項衝突，請選擇最終採用的值
              </p>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-96">
              <div className="space-y-4">
                {conflicts.map((conflict) => (
                  <div key={conflict.itemId} className="border border-red-200 rounded-lg p-4 bg-red-50">
                    <div className="mb-3">
                      <h4 className="font-medium text-gray-900">衝突項目</h4>
                      <p className="text-sm text-gray-600">{conflict.fieldPath}</p>
                    </div>

                    {/* 三方對比 */}
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">提案預期值</span>
                        <div className="p-2 bg-gray-100 rounded text-xs">
                          {formatValue(conflict.expectedValue)}
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-orange-600 block mb-1">檔案當前值</span>
                        <div className="p-2 bg-orange-100 rounded text-xs">
                          {formatValue(conflict.currentLiveValue)}
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-blue-600 block mb-1">提案新值</span>
                        <div className="p-2 bg-blue-100 rounded text-xs">
                          {formatValue(conflict.proposedValue)}
                        </div>
                      </div>
                    </div>

                    {/* 最終決策輸入 */}
                    <div className="mt-3">
                      <label className="text-xs text-gray-700 block mb-1">最終採用值:</label>
                      <input
                        type="text"
                        value={conflictResolutions[conflict.itemId] ?? JSON.stringify(conflict.proposedValue)}
                        onChange={(e) => setConflictResolutions(prev => ({
                          ...prev,
                          [conflict.itemId]: e.target.value
                        }))}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="px-6 py-4 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowConflictResolution(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                取消
              </button>
              <button
                onClick={handleResolveConflicts}
                disabled={isProcessing}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {isProcessing ? '處理中...' : '確認並套用變更'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewDetail;